"use client";

import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
	ArrowRight,
	BriefcaseBusiness,
	Building2,
	HandHeart,
	Handshake,
	Landmark,
	ShieldCheck,
	Sparkles,
} from "lucide-react";

import { FEATURED, HotelCard } from "@/components/home/portfolio-section";
import { useLang, useLangPrefix } from "@/contexts/lang";
import { hotels } from "@/data/home";

export const Route = createFileRoute("/about")({
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=86400, stale-while-revalidate=604800",
	}),
	head: () => ({
		meta: [
			{ title: "За нас | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Дознајте повеќе за Македонијатурист АД, нашата историја, мисија и вредности. Водечка хотелска групација во Македонија од 1963 година.",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/about",
			},
		],
	}),
	component: AboutPage,
});

const goalIcons: LucideIcon[] = [HandHeart, Landmark, BriefcaseBusiness];
const brandValueIcons: LucideIcon[] = [
	HandHeart,
	ShieldCheck,
	Building2,
	BriefcaseBusiness,
	Sparkles,
	Handshake,
];

export function AboutPage() {
	const t = useLang();
	const prefix = useLangPrefix();
	const featured = FEATURED.flatMap((name) => {
		const h = hotels.find((x) => x.name === name);
		return h ? [h] : [];
	});

	return (
		<main className="bg-background">
			{/* Hero */}
			<section className="mt-grain relative overflow-hidden bg-primary text-white">
				{/* Animated background elements */}
				<div className="pointer-events-none absolute inset-0">
					{/* Main gold glow - animated */}
					<motion.div
						className="absolute -top-[20%] right-0 h-[400px] w-[400px] sm:h-[600px] sm:w-[600px] md:h-[800px] md:w-[800px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.15),transparent)] blur-[60px] sm:blur-[80px] md:blur-[100px]"
						animate={{
							scale: [1, 1.2, 1],
							opacity: [0.5, 0.8, 0.5],
						}}
						transition={{
							duration: 8,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					{/* Secondary white glow - animated */}
					<motion.div
						className="absolute bottom-0 left-[-10%] h-[300px] w-[300px] sm:h-[450px] sm:w-[450px] md:h-[600px] md:w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.08),transparent)] blur-[50px] sm:blur-[65px] md:blur-[80px]"
						animate={{
							scale: [1, 1.15, 1],
							x: [0, 30, 0],
						}}
						transition={{
							duration: 10,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					{/* Additional floating accent */}
					<motion.div
						className="absolute top-[40%] left-[20%] h-[150px] w-[150px] sm:h-[225px] sm:w-[225px] md:h-[300px] md:w-[300px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.08),transparent)] blur-[40px] sm:blur-[50px] md:blur-[60px]"
						animate={{
							y: [-20, 20, -20],
							x: [-10, 10, -10],
						}}
						transition={{
							duration: 12,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					{/* Subtle grid overlay */}
					<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] sm:bg-[size:50px_50px] md:bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
				</div>
				<div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-28">
					<div className="max-w-4xl">
						<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
							{t.aboutPage.hero.label}
						</p>
						<h1 className="mt-4 sm:mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-medium tracking-tight leading-[1.08]">
							{t.aboutPage.hero.heading}
						</h1>
						<p className="mt-4 sm:mt-6 max-w-3xl text-base sm:text-lg text-white/80 font-light leading-relaxed md:text-xl">
							{t.aboutPage.hero.intro}
						</p>

						<dl className="mt-8 sm:mt-12 grid gap-6 sm:gap-8 border-t border-white/10 pt-6 sm:pt-8 grid-cols-2 sm:grid-cols-3 max-w-3xl">
							<HeroStat
								label={t.aboutPage.hero.portfolioLabel}
								value={`${hotels.length} ${t.aboutPage.hero.portfolioValue}`}
							/>
							<HeroStat
								label={t.aboutPage.hero.locationLabel}
								value={t.aboutPage.hero.locationValue}
							/>
							<HeroStat
								label={t.aboutPage.hero.focusLabel}
								value={t.aboutPage.hero.focusValue}
							/>
						</dl>
					</div>
				</div>
			</section>

			{/* Story + audiences */}
			<section className="relative overflow-hidden bg-secondary py-12 sm:py-16 md:py-28">
				<div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/3 -skew-x-12 bg-background/60 md:block" />
				<div className="relative mx-auto max-w-7xl px-4 sm:px-6">
					<div className="grid gap-8 md:gap-14 md:grid-cols-12 md:items-start">
						<div className="md:col-span-5">
							<span className="inline-block h-1 w-12 bg-accent mb-4 md:mb-6" />
							<h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-primary leading-[1.1]">
								{t.aboutPage.story.heading1}
								<br />
								{t.aboutPage.story.heading2}
							</h2>
						</div>

						<div className="md:col-span-7 md:pl-12">
							<div className="space-y-4 sm:space-y-6">
								<p className="text-base sm:text-lg text-foreground/80 font-light leading-relaxed">
									{t.aboutPage.story.p1}
								</p>
								<p className="text-base sm:text-lg text-foreground/80 font-light leading-relaxed">
									{t.aboutPage.story.p2}
								</p>
							</div>

							<div className="mt-6 sm:mt-10 rounded-xl md:rounded-2xl border border-primary/10 bg-background/60 p-5 sm:p-8">
								<p className="text-xs font-bold tracking-[0.2em] uppercase text-primary">
									{t.aboutPage.story.forWhoLabel}
								</p>
								<ul className="mt-4 sm:mt-6 grid gap-3 text-sm text-foreground/80 font-light sm:grid-cols-2">
									{t.aboutPage.audiences.map((audience) => (
										<li key={audience} className="flex gap-3">
											<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
											<span>{audience}</span>
										</li>
									))}
								</ul>
							</div>
						</div>
					</div>

					<div className="mt-10 sm:mt-16 grid gap-4 sm:gap-6 md:grid-cols-3">
						{t.aboutPage.goals.map((goal, idx) => {
							const GoalIcon = goalIcons[idx] ?? HandHeart;
							return (
								<div
									key={goal.title}
									className="rounded-xl md:rounded-2xl border border-border bg-card p-5 sm:p-8 shadow-sm"
								>
									<div className="flex items-start gap-3 sm:gap-4">
										<div className="inline-flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary">
											<GoalIcon className="h-4 w-4 sm:h-5 sm:w-5" />
										</div>
										<div>
											<h3 className="font-display text-lg sm:text-xl font-medium text-primary">
												{goal.title}
											</h3>
											<p className="mt-2 text-sm text-foreground/80 font-light leading-relaxed">
												{goal.description}
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</section>

			{/* Portfolio */}
			<section className="bg-background py-12 sm:py-16 md:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<div className="flex flex-col gap-6 md:gap-8 md:flex-row md:items-end md:justify-between">
						<div className="max-w-2xl">
							<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent mb-4 md:mb-6">
								{t.aboutPage.portfolioSection.label}
							</p>
							<h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-primary leading-[1.1]">
								{t.aboutPage.portfolioSection.heading}
							</h2>
							<p className="mt-4 sm:mt-6 text-base sm:text-lg text-foreground/80 font-light leading-relaxed">
								{t.aboutPage.portfolioSection.intro}
							</p>
						</div>

						<div className="flex flex-col gap-3 sm:flex-row">
							<a
								href={`${prefix}/#portfolio`}
								className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold text-white transition-all hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20"
							>
								{t.aboutPage.portfolioSection.viewPortfolio}
								<ArrowRight className="h-4 w-4" />
							</a>
							<a
								href={`${prefix}/contact`}
								className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-5 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold text-primary transition-colors hover:bg-secondary"
							>
								{t.aboutPage.portfolioSection.contact}
							</a>
						</div>
					</div>

					<div className="-mx-4 sm:-mx-6 lg:mx-0 overflow-x-auto px-4 sm:px-6 lg:px-0 pb-2">
						<div className="mt-8 sm:mt-12 grid grid-cols-4 gap-5 min-w-[800px] lg:min-w-0">
							{featured.map((hotel) => (
								<HotelCard key={hotel.name} hotel={hotel} />
							))}
						</div>
					</div>

					<div className="mt-10 sm:mt-14 rounded-xl md:rounded-2xl border border-border bg-secondary p-5 sm:p-8 md:p-10">
						<div className="grid gap-6 md:gap-8 md:grid-cols-12 md:items-center">
							<div className="md:col-span-8">
								<h3 className="font-display text-xl sm:text-2xl font-medium text-primary">
									{t.aboutPage.portfolioSection.brandArchTitle}
								</h3>
								<p className="mt-2 sm:mt-3 text-sm text-foreground/80 font-light leading-relaxed">
									{t.aboutPage.portfolioSection.brandArchDesc}
								</p>
							</div>
							<div className="md:col-span-4 md:text-right">
								<p className="text-xs font-bold tracking-[0.2em] uppercase text-primary">
									{t.aboutPage.portfolioSection.brandArchNote}
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Values */}
			<section className="mt-grain bg-primary py-12 sm:py-16 md:py-24 text-white">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<div className="max-w-3xl">
						<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent mb-4 md:mb-6">
							{t.aboutPage.valuesSection.label}
						</p>
						<h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.1]">
							{t.aboutPage.valuesSection.heading}
						</h2>
						<p className="mt-4 sm:mt-6 text-base sm:text-lg text-white/70 font-light leading-relaxed">
							{t.aboutPage.valuesSection.intro}
						</p>
					</div>

					<div className="mt-8 sm:mt-12 grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
						{t.aboutPage.brandValues.map((value, idx) => {
							const ValueIcon = brandValueIcons[idx] ?? HandHeart;
							return (
								<div
									key={value.title}
									className="rounded-xl md:rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-8 backdrop-blur-sm"
								>
									<div className="flex items-start gap-3 sm:gap-4">
										<div className="mt-0.5 inline-flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full bg-accent/20 text-accent">
											<ValueIcon className="h-4 w-4 sm:h-5 sm:w-5" />
										</div>
										<div>
											<h3 className="text-base sm:text-lg font-medium">
												{value.title}
											</h3>
											<p className="mt-2 text-sm text-white/70 font-light leading-relaxed">
												{value.description}
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>

					<div className="mt-10 sm:mt-14 flex flex-col gap-4 border-t border-white/10 pt-6 sm:pt-8 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm text-white/70 font-light">
							{t.aboutPage.valuesSection.contactNote}
						</p>
						<a
							href={`${prefix}/contact`}
							className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-5 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold text-primary transition-all hover:bg-accent/90 hover:shadow-lg hover:shadow-accent/20"
						>
							{t.aboutPage.valuesSection.contactCta}{" "}
							<ArrowRight className="h-4 w-4" />
						</a>
					</div>
				</div>
			</section>
		</main>
	);
}

function HeroStat({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-xs font-bold tracking-[0.2em] uppercase text-white/60">
				{label}
			</dt>
			<dd className="mt-2 text-sm font-medium text-white">{value}</dd>
		</div>
	);
}
